// Fatura do pedido — jsPDF com identidade laranja & branco
interface PDFOrderData {
  order: any
  customerName: string
  customerPhone: string
  paymentMethod: string
}

const LARANJA = [234, 88, 12] as const
const LARANJA_CLARO = [255, 237, 213] as const
const TEXTO = [28, 25, 23] as const
const CINZA = [120, 113, 108] as const

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("pt-AO", {
    style: "currency",
    currency: "AOA",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

export async function downloadOrderPDF(data: PDFOrderData): Promise<void> {
  try {
    const { jsPDF } = await import("jspdf")
    const doc = new jsPDF({ unit: "mm", format: "a4" })

    const { order, customerName, customerPhone } = data
    const pageWidth = doc.internal.pageSize.width
    const pageHeight = doc.internal.pageSize.height
    const margin = 18
    const contentWidth = pageWidth - margin * 2

    // ===== Cabeçalho laranja =====
    doc.setFillColor(LARANJA[0], LARANJA[1], LARANJA[2])
    doc.rect(0, 0, pageWidth, 52, "F")
    // Faixa decorativa clara
    doc.setFillColor(255, 255, 255)
    doc.circle(pageWidth - 14, -6, 22, "F")

    doc.setTextColor(255, 255, 255)
    doc.setFont("helvetica", "bold")
    doc.setFontSize(26)
    doc.text("FATURA", margin, 24)

    doc.setFont("helvetica", "normal")
    doc.setFontSize(11)
    doc.text("Syncmenu · Comprovativo de Pedido", margin, 34)

    doc.setFont("helvetica", "bold")
    doc.setFontSize(13)
    doc.text(`Nº ${String(order._id || "").slice(-8).toUpperCase()}`, pageWidth - margin, 24, {
      align: "right",
    })
    const emitidaEm = new Date(order.createdAt || Date.now())
    doc.setFont("helvetica", "normal")
    doc.setFontSize(9)
    doc.text(
      `${emitidaEm.toLocaleDateString("pt-AO")} às ${emitidaEm.toLocaleTimeString("pt-AO", { hour: "2-digit", minute: "2-digit" })}`,
      pageWidth - margin,
      32,
      { align: "right" },
    )

    let y = 70

    // ===== Bloco cliente / pagamento =====
    const blocoH = 30
    doc.setFillColor(250, 250, 249)
    doc.roundedRect(margin, y, contentWidth / 2 - 3, blocoH, 3, 3, "F")
    doc.roundedRect(pageWidth / 2 + 3, y, contentWidth / 2 - 3, blocoH, 3, 3, "F")

    doc.setTextColor(CINZA[0], CINZA[1], CINZA[2])
    doc.setFontSize(7.5)
    doc.setFont("helvetica", "bold")
    doc.text("CLIENTE", margin + 6, y + 8)
    doc.text("PAGAMENTO", pageWidth / 2 + 9, y + 8)

    doc.setTextColor(TEXTO[0], TEXTO[1], TEXTO[2])
    doc.setFont("helvetica", "normal")
    doc.setFontSize(10.5)
    doc.text(customerName || "Cliente", margin + 6, y + 16)
    doc.text(customerPhone || "—", margin + 6, y + 23)
    doc.text(data.paymentMethod || "Na entrega", pageWidth / 2 + 9, y + 16)
    doc.text(`Estado: ${order.status || "Concluído"}`, pageWidth / 2 + 9, y + 23)

    y += blocoH + 12

    // ===== Tabela de itens =====
    doc.setFillColor(LARANJA[0], LARANJA[1], LARANJA[2])
    doc.roundedRect(margin, y, contentWidth, 9, 2, 2, "F")
    doc.setTextColor(255, 255, 255)
    doc.setFont("helvetica", "bold")
    doc.setFontSize(9)
    doc.text("DESCRIÇÃO", margin + 4, y + 6)
    doc.text("QTD", pageWidth - margin - 42, y + 6, { align: "right" })
    doc.text("PREÇO", pageWidth - margin - 24, y + 6, { align: "right" })
    doc.text("TOTAL", pageWidth - margin - 4, y + 6, { align: "right" })
    y += 15

    type LinhaTabela = { nome: string; qtd: number; preco: number }
    const linhas: LinhaTabela[] = []

    for (const item of order.itens || []) {
      linhas.push({
        nome: item.prato?.nome || item.nome || "Item",
        qtd: item.quantidade || 1,
        preco: item.prato?.preco ?? item.preco ?? 0,
      })
    }

    for (const b of (order as any).buffetes || []) {
      linhas.push({
        nome: `Bufete · ${b.buffeteId?.nome || b.nome || ""}`,
        qtd: b.quantidade || 1,
        preco: b.preco_unitario || 0,
      })
    }

    let totalGeral = 0
    doc.setFont("helvetica", "normal")
    doc.setFontSize(10)

    linhas.forEach((linha, index) => {
      if (y > pageHeight - 60) {
        doc.addPage()
        y = margin + 10
      }

      const totalLinha = linha.preco * linha.qtd
      totalGeral += totalLinha

      if (index % 2 === 0) {
        doc.setFillColor(250, 250, 249)
        doc.rect(margin, y - 5, contentWidth, 11, "F")
      }

      doc.setTextColor(TEXTO[0], TEXTO[1], TEXTO[2])
      const nomeCurto =
        linha.nome.length > 44 ? `${linha.nome.slice(0, 41)}...` : linha.nome
      doc.text(nomeCurto, margin + 4, y + 1)
      doc.text(String(linha.qtd), pageWidth - margin - 42, y + 1, { align: "right" })
      doc.text(formatCurrency(linha.preco), pageWidth - margin - 24, y + 1, { align: "right" })
      doc.setFont("helvetica", "bold")
      doc.text(formatCurrency(totalLinha), pageWidth - margin - 4, y + 1, { align: "right" })
      doc.setFont("helvetica", "normal")
      y += 11
    })

    y += 6

    // ===== Observações =====
    if ((order as any).observacoes) {
      doc.setTextColor(CINZA[0], CINZA[1], CINZA[2])
      doc.setFontSize(9)
      doc.text(`Sugestões do cliente: ${(order as any).observacoes}`, margin, y, {
        maxWidth: contentWidth,
      })
      y += 12
    }

    if (y > pageHeight - 70) {
      doc.addPage()
      y = margin + 10
    }

    // ===== Total =====
    doc.setFillColor(LARANJA_CLARO[0], LARANJA_CLARO[1], LARANJA_CLARO[2])
    doc.roundedRect(margin, y, contentWidth, 16, 3, 3, "F")
    doc.setTextColor(TEXTO[0], TEXTO[1], TEXTO[2])
    doc.setFont("helvetica", "bold")
    doc.setFontSize(13)
    doc.text("TOTAL", margin + 5, y + 10.5)
    doc.setTextColor(LARANJA[0], LARANJA[1], LARANJA[2])
    doc.setFontSize(15)
    doc.text(formatCurrency(totalGeral), pageWidth - margin - 5, y + 10.5, { align: "right" })
    y += 30

    // ===== Rodapé =====
    doc.setTextColor(CINZA[0], CINZA[1], CINZA[2])
    doc.setFont("helvetica", "normal")
    doc.setFontSize(9.5)
    doc.text("Obrigado pela preferência — bom apetite!", pageWidth / 2, y, { align: "center" })
    doc.setFontSize(8)
    doc.text(
      "Documento gerado automaticamente pelo Syncmenu após a confirmação do pedido.",
      pageWidth / 2,
      y + 7,
      { align: "center" },
    )

    const fileName = `fatura_${String(order._id || "pedido").slice(-8)}_${new Date().toISOString().slice(0, 10)}.pdf`
    doc.save(fileName)
  } catch (error) {
    console.error("Erro ao gerar PDF:", error)
    fallbackDownload(data)
  }
}

function fallbackDownload(data: PDFOrderData): void {
  const { order, customerName, customerPhone, paymentMethod } = data
  const items = order.itens || []
  const totalBuffetes = ((order as any).buffetes || []).reduce(
    (s: number, b: any) => s + (b.subtotal || 0),
    0,
  )
  const total =
    items.reduce((sum: number, item: any) => {
      const preco = item.prato?.preco || item.preco || 0
      return sum + preco * (item.quantidade || 1)
    }, 0) + totalBuffetes

  const texto = [
    "Syncmenu — FATURA DE PEDIDO",
    `Número: #${String(order._id).slice(-8).toUpperCase()}`,
    `Cliente: ${customerName} · ${customerPhone}`,
    `Pagamento: ${paymentMethod || "Na entrega"}`,
    "",
    ...items.map(
      (item: any) =>
        `${item.quantidade}x ${item.prato?.nome || item.nome} — ${formatCurrency((item.prato?.preco || item.preco || 0) * (item.quantidade || 1))}`,
    ),
    ...((order as any).buffetes || []).map(
      (b: any) => `${b.quantidade}x Bufete ${b.buffeteId?.nome || b.nome} — ${formatCurrency(b.subtotal)}`,
    ),
    "",
    `TOTAL: ${formatCurrency(total)}`,
    "Obrigado pela preferência!",
  ].join("\n")

  const blob = new Blob([texto], { type: "text/plain;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = `fatura_${String(order._id).slice(-8)}.txt`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
